import { BiCatalogField } from '@euler/model/bi/catalog';
import { BiFilter, BiFilterOp } from '@euler/model/bi/query';
import { BiFieldType } from '@euler/model/bi/result';

/** The operators a coded field offers: its values are picked, not typed. */
const kBiCodedFilterOps = [
  BiFilterOp.In,
  BiFilterOp.NotIn,
  BiFilterOp.Eq,
  BiFilterOp.Neq,
];

/** Whether the form picks the field's values from its labels. */
export function isBiCodedField(field: BiCatalogField): boolean {
  return !!field.valueLabels && Object.keys(field.valueLabels).length > 0;
}

/** The operators the form offers for a field. */
export function biFilterOpsFor(field: BiCatalogField): BiFilterOp[] {
  if (isBiCodedField(field)) return kBiCodedFilterOps;
  switch (field.type) {
    case BiFieldType.Number:
      return [
        BiFilterOp.Eq,
        BiFilterOp.Neq,
        BiFilterOp.Gt,
        BiFilterOp.Gte,
        BiFilterOp.Lt,
        BiFilterOp.Lte,
      ];
    case BiFieldType.Boolean:
      return [BiFilterOp.Eq];
    case BiFieldType.String:
      return [
        BiFilterOp.Eq,
        BiFilterOp.Neq,
        BiFilterOp.Contains,
        BiFilterOp.In,
        BiFilterOp.NotIn,
      ];
    default:
      return [BiFilterOp.Eq, BiFilterOp.Gte, BiFilterOp.Lte];
  }
}

/**
 * The filter the form describes, or null while the value does not fit the
 * field: a number field needs numbers; a list takes `、` or comma
 * separated values.
 */
export function biFilterFromForm(
  field: BiCatalogField,
  op: BiFilterOp,
  text: string,
): BiFilter | null {
  const value = text.trim();
  if (!value) return null;
  const numeric = field.type === BiFieldType.Number;
  const scalar = (raw: string): string | number | null => {
    if (!numeric) return raw;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  };
  switch (op) {
    case BiFilterOp.In:
    case BiFilterOp.NotIn: {
      const values = value
        .split(/[,，、]/)
        .map(entry => entry.trim())
        .filter(Boolean)
        .map(scalar);
      if (!values.length || values.some(entry => entry === null)) return null;
      return { field: field.key, op, values: values as (string | number)[] };
    }
    case BiFilterOp.Contains:
      return { field: field.key, op, value };
    case BiFilterOp.Eq:
    case BiFilterOp.Neq:
    case BiFilterOp.Gt:
    case BiFilterOp.Gte:
    case BiFilterOp.Lt:
    case BiFilterOp.Lte: {
      if (field.type === BiFieldType.Boolean)
        return {
          field: field.key,
          op,
          value: value === '是' || value === 'true',
        };
      const parsed = scalar(value);
      return parsed === null ? null : { field: field.key, op, value: parsed };
    }
    default:
      return null;
  }
}

/**
 * The filter a coded field's picked codes describe: one for 等于 / 不等于,
 * any number for 属于 / 不属于; null when the pick does not fit the
 * operator.
 */
export function biCodedFilterFromPick(
  field: BiCatalogField,
  op: BiFilterOp,
  codes: string[],
): BiFilter | null {
  const single = op === BiFilterOp.Eq || op === BiFilterOp.Neq;
  if (!codes.length || (single && codes.length !== 1)) return null;
  const values = codes.map(code =>
    field.type === BiFieldType.Number ? Number(code) : code,
  );
  if (
    values.some(value => typeof value === 'number' && !Number.isFinite(value))
  )
    return null;
  switch (op) {
    case BiFilterOp.In:
    case BiFilterOp.NotIn:
      return { field: field.key, op, values };
    case BiFilterOp.Eq:
    case BiFilterOp.Neq:
      return { field: field.key, op, value: values[0] };
    default:
      return null;
  }
}

/** A pick after tapping a code: one operator keeps one code, a list toggles. */
export function biTogglePickedCode(
  op: BiFilterOp,
  codes: string[],
  code: string,
): string[] {
  if (codes.includes(code)) return codes.filter(entry => entry !== code);
  return op === BiFilterOp.Eq || op === BiFilterOp.Neq
    ? [code]
    : [...codes, code];
}
